import { useRef, useEffect, useCallback, useMemo } from 'react'
import { useAppSelector, shallowEqual } from './reduxHooks'

/**
 * A custom hook that allows you to wait for certain conditions in the Redux store.
 * Listeners can be added via the `waitForResult` method.
 * Each listener will be called when the selector returns a value that is not undefined, and will then be unregistered.
 *
 * @param selector The selector function to use to get a result from the store. Takes the Redux state as an argument. Return type must be an object or `undefined`.
 * @returns An object with an `awaitResult` method. `awaitResult` returns a promise that will resolve with the value from the selector, when the selector returns a defined object.
 */
function useReduxStateListener<Result extends {}>(
  selector: (state: App.State) => Result | undefined,
) {
  const latestResult = useRef<Result | undefined>(undefined)
  const result = useAppSelector(selector, shallowEqual)

  const listeners = useRef<Array<(result: Result) => void>>([])

  const addListener = useCallback((listener: (result: Result) => void, resolveImmediatelyIfAlreadyHasResult: boolean = false) => {
    if (latestResult.current && resolveImmediatelyIfAlreadyHasResult) {
      listener(latestResult.current)
    } else {
      listeners.current.push(listener)
    }
  }, [])

  const addListenerPromise = useCallback((resolveImmediatelyIfAlreadyHasResult: boolean = false) => {
    return new Promise<Result>(resolve => {
      addListener(resolve, resolveImmediatelyIfAlreadyHasResult)
    })
  }, [addListener])

  useEffect(() => {
    if (result) {
      listeners.current.forEach(listener => listener(result))
      listeners.current = []
    }
    latestResult.current = result
  }, [result])

  return useMemo(() => ({
    awaitResult: addListenerPromise,
  }), [addListenerPromise])
}

export default useReduxStateListener
